import { useMemo } from 'react';
import useAuth from 'src/hooks/useAuth';
import useCompanyTreeQuery from 'src/services/work_area/useCompanyTreeQuery';
import useCompanyUsersSearchQuery from 'src/services/work_area/useCompanyUsersSearchQuery';
import useCompanyUsersTreeQuery from 'src/services/work_area/useCompanyUsersTreeQuery';
import {
  CompanyNode,
  CompanyTreeFilterBy,
  CompanyTreeResponse,
  UserTreeFilterBy,
  UserTreeNode,
  UserTreeResponse,
} from 'src/types/work_area';
import { useContextSelector } from 'use-context-selector';
import { getFilterByFromOptionValue } from './useWorkAreaFilterOptions';
import { WorkAreaContext } from './WorkAreaContext';

const matchesCompanyFilter = (node: CompanyNode, filterBy: CompanyTreeFilterBy): boolean => {
  if (filterBy === 'ShowActive') {
    return !node.isDeleted;
  }
  if (filterBy === 'ShowDisabled') {
    return node.isDeleted;
  }
  return true;
};

const filterCompanyTree = (
  node: CompanyNode,
  filterBy: CompanyTreeFilterBy
): CompanyNode | null => {
  const filteredChildren =
    node.children?.map((child) => filterCompanyTree(child, filterBy)).filter(Boolean) ?? [];
  const matches = matchesCompanyFilter(node, filterBy);
  if (!matches && filteredChildren.length === 0) {
    return null;
  }
  return {
    ...node,
    children: filteredChildren as CompanyNode[],
    hasChildren: filteredChildren.length > 0,
  };
};

const countCompanyNodes = (node: CompanyNode | null): number => {
  if (!node) {
    return 0;
  }
  return 1 + (node.children?.reduce((total, child) => total + countCompanyNodes(child), 0) ?? 0);
};

const matchesUserFilter = (node: UserTreeNode, filterBy: UserTreeFilterBy): boolean => {
  if (filterBy === 'ShowActive') {
    return !node.isDeleted;
  }
  if (filterBy === 'ShowDisabled') {
    return node.isDeleted;
  }
  return true;
};

const filterUserTree = (node: UserTreeNode, filterBy: UserTreeFilterBy): UserTreeNode | null => {
  const filteredChildren =
    node.children?.map((child) => filterUserTree(child, filterBy)).filter(Boolean) ?? [];
  const matches = matchesUserFilter(node, filterBy);
  if (!matches && filteredChildren.length === 0) {
    return null;
  }
  return {
    ...node,
    children: filteredChildren as UserTreeNode[],
    hasChildren: filteredChildren.length > 0,
  };
};

const countUserNodes = (node: UserTreeNode | null): number => {
  if (!node) {
    return 0;
  }
  return 1 + (node.children?.reduce((total, child) => total + countUserNodes(child), 0) ?? 0);
};

export function useWorkAreaCompanyTree() {
  const { user } = useAuth();
  const parsed = user?.LoggedInCompanyId != null ? Number(user.LoggedInCompanyId) : NaN;
  const rootCompanyId = Number.isNaN(parsed) ? null : parsed;
  const companyTreeFilter = useContextSelector(
    WorkAreaContext,
    (context) => context.companyTreeFilter
  );

  const { data: raw, ...rest } = useCompanyTreeQuery(rootCompanyId);

  const filterBy = useMemo<CompanyTreeFilterBy>(
    () => getFilterByFromOptionValue(companyTreeFilter?.value),
    [companyTreeFilter?.value]
  );

  const data = useMemo((): CompanyTreeResponse | undefined => {
    if (!raw?.root) {
      return undefined;
    }
    const filteredRoot = filterCompanyTree(raw.root, filterBy);
    if (filteredRoot == null) {
      return {
        visibilityLevelUsed: raw.visibilityLevelUsed,
        totalNodes: 0,
        root: undefined,
      };
    }
    return {
      visibilityLevelUsed: raw.visibilityLevelUsed,
      totalNodes: countCompanyNodes(filteredRoot),
      root: filteredRoot,
    };
  }, [raw, filterBy]);

  return { ...rest, data };
}

export function useWorkAreaCompanyUsersTree() {
  const selectedCompanyId = useContextSelector(
    WorkAreaContext,
    (context) => context.selectedCompanyId
  );
  const userCompaniesFilter = useContextSelector(
    WorkAreaContext,
    (context) => context.userCompaniesFilter
  );

  const { data: raw, ...rest } = useCompanyUsersTreeQuery(selectedCompanyId);

  const filterBy = useMemo(
    () => getFilterByFromOptionValue(userCompaniesFilter?.value),
    [userCompaniesFilter?.value]
  );

  const data = useMemo((): UserTreeResponse | undefined => {
    const root = raw?.nodes?.[0];
    if (!root) {
      return undefined;
    }
    const filteredRoot = filterUserTree(root, filterBy);
    return {
      totalNodes: countUserNodes(filteredRoot),
      nodes: filteredRoot != null ? [filteredRoot] : [],
    };
  }, [raw, filterBy]);

  return { data, ...rest };
}

/**
 * Resolves companyId for ExportTree when search scope has no root (super admin all-companies).
 * Uses LoggedInCompanyId (same root as company tree); otherwise selected company or user CompanyId.
 */
export function resolveWorkAreaSearchExportCompanyId(
  searchRootCompanyId: number | null,
  isSuperAdminSession: boolean,
  user: { CompanyId?: string | number; LoggedInCompanyId?: string | number } | null | undefined
): number | null {
  if (searchRootCompanyId != null) {
    return searchRootCompanyId;
  }
  if (isSuperAdminSession) {
    const parsed = user?.LoggedInCompanyId != null ? Number(user.LoggedInCompanyId) : NaN;
    return Number.isNaN(parsed) ? null : parsed;
  }
  const parsed = user?.CompanyId != null ? Number(user.CompanyId) : NaN;
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Company users search. Root company for search is:
 * - rootCompanyIdOverride when provided (e.g. from modal opened inside Work Area)
 * - Otherwise: selected company in CompanyTree when one is selected, else logged-in user's company
 */
export function useWorkAreaCompanyUsersSearch(
  searchTerm: string,
  rootCompanyIdOverride?: number | null
) {
  const { isLoggedInAsSuperAdmin, user } = useAuth();
  const selectedCompanyId = useContextSelector(
    WorkAreaContext,
    (context) => context.selectedCompanyId
  );

  const effectiveRootCompanyId = useMemo((): number | null => {
    if (rootCompanyIdOverride !== undefined) {
      return rootCompanyIdOverride ?? null;
    }
    if (selectedCompanyId != null) {
      return selectedCompanyId;
    }
    if (isLoggedInAsSuperAdmin) {
      return null;
    }
    if (user?.LoggedInCompanyId != null) {
      const parsed = Number(user.CompanyId);
      return Number.isNaN(parsed) ? null : parsed;
    }
    return null;
  }, [
    rootCompanyIdOverride,
    selectedCompanyId,
    isLoggedInAsSuperAdmin,
    user?.LoggedInCompanyId,
    user.CompanyId,
  ]);

  const { data: raw, ...rest } = useCompanyUsersSearchQuery(searchTerm, effectiveRootCompanyId);

  const data = raw?.data ?? [];

  return { data, ...rest };
}
